import { useEffect, useState } from "react";
import AppShell from "../components/AppShell";
import VideoTable from "../components/VideoTable";
import { getVideos } from "../services/videos";
import type {  DashboardRecording } from "../types/listOfRecordingsTypes";

import { useNavigate } from "react-router-dom";
import MultiSelectFilter from "../components/filters/MultiSelectFilter";

function VideoListPage() {
  const [recordings, setRecordings] = useState<DashboardRecording[]>([]);
  const [selectedPatients, setSelectedPatients] = useState<string[]>([]);
  const [selectedReviewers, setSelectedReviewers] = useState<string[]>([]);
  const [selectedAiScores, setSelectedAiScores] = useState<string[]>([]);
  const [selectedReviewerScores, setSelectedReviewerScores] = useState<string[]>([]);
  const [selectedRewards, setSelectedRewards] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(["Review Now"]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] =useState(1);
  const navigate = useNavigate();

  const PAGE_SIZE = 10;

  async function loadVideos() {
    try {
      setLoading(true);
      setError("");

      const projectId = sessionStorage.getItem("selectedProjectId");
      if (!projectId) {
        throw new Error("Project not selected");
      }
      const response = await getVideos(projectId);

      const mappedRecordings: DashboardRecording[] =
        response.data.recordings.map((recording) => {
          /*
          * Flatten all AI-processing records because one session
          * can contain multiple videoMeta entries.
          */
          const processingCandidates =
            recording.videoMeta.reduce(
              (items, videoMeta) => [
                ...items,
                ...videoMeta.aiProcessing,
              ],
              [] as typeof recording.videoMeta[number]["aiProcessing"]
            );

          /*
          * Prefer a completed record containing AI result data.
          * Otherwise use the first available processing record.
          */
          const aiProcessing =
            processingCandidates.find(
              (item) =>
                item.Status === "COMPLETED" &&
                item.Data !== null
            ) ??
            processingCandidates[0] ??
            null;

          const aiData =
            aiProcessing?.Data ?? null;

          const review =
            aiProcessing?.aiVideoReview?.[0] ??
            null;

          const reviewer =
            review?.aiVideoReviewer ?? null;

          const patientName = [
            recording.user?.FirstName,
            recording.user?.MiddleName,
            recording.user?.LastName,
          ]
            .filter(Boolean)
            .join(" ");

          const reviewedBy = reviewer
            ? [
                reviewer.FirstName,
                reviewer.MiddleName,
                reviewer.LastName,
              ]
                .filter(Boolean)
                .join(" ")
            : "-";

          const reviewStatus: DashboardRecording["reviewStatus"] =
            !review
              ? "Review Now"
              : review.Status === "REJECTED"
                ? "Rejected"
                : "Accepted";

          return {
            sessionId:
              aiProcessing?.SessionId ??
              recording.Id,

            patientId:
              recording.UserId,

            patientName:
              patientName ||
              "Unknown Patient",

            submittedAt:
              recording.StartedAt,

            reviewedAt:
              review?.ReviewedAt ?? null,

            reviewedBy,

            reviewerScore:
              review?.OverAll ?? "-",

            reviewStatus,

            aiScore:
              aiData?.overall_score ?? "-",

            rewards: "-",
          };
        });

      setRecordings(mappedRecordings);
      setCurrentPage(1);
    } catch (err) {
      console.error(
        "loadVideos error:",
        err
      );

      setError(
        err instanceof Error
          ? err.message
          : "Failed to load video processing results"
      );

      setRecordings([]);
      setCurrentPage(1);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadVideos();
  }, []);

  const patientOptions = Array.from(
    new Set(
      recordings
        .map((record) => record.patientName)
        .filter(Boolean)
    )
  );

  const reviewerOptions = Array.from(
    new Set(
      recordings
        .map((record) => record.reviewedBy)
        .filter(
          (reviewer) =>
            reviewer && reviewer !== "-"
        )
    )
  );

  const aiScoreOptions = Array.from(
    new Set(
      recordings
        .map((record) => String(record.aiScore))
        .filter((score) => score !== "-")
    )
  );

  const reviewerScoreOptions = Array.from(
    new Set(
      recordings
        .map((record) =>
          String(record.reviewerScore)
        )
        .filter((score) => score !== "-")
    )
  );

  const rewardOptions = Array.from(
    new Set(
      recordings
        .map((record) => record.rewards)
        .filter(
          (reward) => reward && reward !== "-"
        )
    )
  );

  const statusOptions = Array.from(
    new Set(
      recordings
        .map((record) => record.reviewStatus)
        .filter(Boolean)
    )
  );

  const filteredRecordings = recordings.filter((recording) => {
      if (
        selectedPatients.length > 0 &&
        !selectedPatients.includes(
          recording.patientName
        )
      ) {
        return false;
      }

      if (
        selectedReviewers.length > 0 &&
        !selectedReviewers.includes(
          recording.reviewedBy
        )
      ) {
        return false;
      }

      if (
        selectedReviewerScores.length > 0 &&
        !selectedReviewerScores.includes(
          String(
            recording.reviewerScore
          )
        )
      ) {
        return false;
      }

      if (
        selectedStatuses.length > 0 &&
        !selectedStatuses.includes(
          recording.reviewStatus
        )
      ) {
        return false;
      }
      return true;
  });

  const totalPages = Math.max(1,
    Math.ceil(
      filteredRecordings.length /
      PAGE_SIZE
    )
  );

  const paginatedRecordings = filteredRecordings.slice((currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  return (
    <AppShell>

      {/* HERO */}
      <section className="dashboard-header">
        <button
          type="button"
          className="dashboard-back-btn"
          onClick={() => navigate("/")}
        >
          {"< Back"}
        </button>

        <h1 className="dashboard-title">
          Care Coordinator Dashboard
        </h1>

        <p className="dashboard-project">
          <strong>Project Name:</strong>{" "}
          Addiction IBH
        </p>
      </section>

      <section className="dashboard-filter-bar">

        <span className="filter-title">
          Filters
        </span>

        <MultiSelectFilter
          placeholder="Patient Name"
          options={patientOptions}
          value={selectedPatients}
          onChange={setSelectedPatients}
        />

        <MultiSelectFilter
          placeholder="Reviewed By"
          options={reviewerOptions}
          value={selectedReviewers}
          onChange={setSelectedReviewers}
        />

        <MultiSelectFilter
          placeholder="AI Score"
          options={aiScoreOptions}
          value={selectedAiScores}
          onChange={setSelectedAiScores}
        />

        <MultiSelectFilter
          placeholder="Reviewer Score"
          options={reviewerScoreOptions}
          value={selectedReviewerScores}
          onChange={setSelectedReviewerScores}
        />

        <MultiSelectFilter
          placeholder="Rewards"
          options={rewardOptions}
          value={selectedRewards}
          onChange={setSelectedRewards}
        />

        <MultiSelectFilter
          placeholder="Review Status"
          options={statusOptions}
          value={selectedStatuses}
          onChange={setSelectedStatuses}
        />

        <button
          type="button"
          className="search-btn"
          onClick={() => {
            setSelectedPatients([]);
            setSelectedReviewers([]);
            setSelectedAiScores([]);
            setSelectedReviewerScores([]);
            setSelectedRewards([]);
            setSelectedStatuses([]);
          }}
        >
          Clear filters
        </button>
        {/* <button
          type="button"
          className="search-btn"
        >
          <span className="search-icon">⌕</span>
          <span>Search</span>
        </button> */}

      </section>

      {/* TABLE SECTION */}
      <section className="">

        {loading && (
          <div className="flex-center loader-container">
            <div className="loading"></div>
          </div>
        )}

        {!loading && error && (
          <div className="empty-state">{error}</div>
        )}

        {!loading && !error && (
          <VideoTable
            recordings={paginatedRecordings}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}

      </section>

    </AppShell>
  );
}

export default VideoListPage;